import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import './styles.css';

const foundingGames = [
  {
    number: '01',
    name: 'Belot',
    region: 'Bulgaria · Balkans',
    status: 'Playable alpha',
    color: 'coral',
  },
  {
    number: '02',
    name: 'Santase',
    region: 'Bulgaria',
    status: 'Daily · AI · Live',
    color: 'teal',
  },
  {
    number: '03',
    name: 'Donka / Hearts',
    region: 'Bulgaria · World',
    status: 'Founding collection',
    color: 'green',
  },
  {
    number: '04',
    name: 'Spades',
    region: 'North America · World',
    status: 'Founding collection',
    color: 'ink',
  },
  {
    number: '05',
    name: 'Bridge',
    region: 'Global clubs',
    status: 'Dedicated track',
    color: 'red',
  },
  {
    number: '06',
    name: 'Baloot',
    region: 'Saudi Arabia · Gulf',
    status: 'Founding collection',
    color: 'saffron',
  },
  {
    number: '07',
    name: 'Thunee',
    region: 'South Africa',
    status: 'Founding collection',
    color: 'leaf',
  },
  {
    number: '08',
    name: 'Truco Argentino',
    region: 'Argentina',
    status: 'Founding collection',
    color: 'cobalt',
  },
];

const proofSteps = [
  ['01', 'Commit', 'The server seals the exact worldwide deal before play.'],
  ['02', 'Play', 'Private cards stay private. The first attempt is immutable.'],
  ['03', 'Reveal', 'The complete board opens after the competition window.'],
  ['04', 'Verify', 'Your device independently checks the published proof.'],
];

function Brand({ light = false }) {
  return (
    <a className={`brand ${light ? 'brand--light' : ''}`} href="#top" aria-label="NAIPERO home">
      <img
        src="/brand/naipero-lockup.png"
        alt="NAIPERO — The World's Card Table."
      />
    </a>
  );
}

function Header() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const close = () => setOpen(false);
    window.addEventListener('resize', close);
    return () => window.removeEventListener('resize', close);
  }, []);

  return (
    <header className="site-header">
      <div className="header-inner">
        <Brand light />
        <button
          className="menu-toggle"
          type="button"
          aria-label="Toggle navigation"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <span />
          <span />
        </button>
        <nav className={open ? 'nav nav--open' : 'nav'} aria-label="Main navigation">
          <a href="#daily" onClick={() => setOpen(false)}>Daily Table</a>
          <a href="#games" onClick={() => setOpen(false)}>Games</a>
          <a href="#fairness" onClick={() => setOpen(false)}>Fairness</a>
          <a className="nav-status" href="#alpha" onClick={() => setOpen(false)}>
            <span /> Android alpha
          </a>
        </nav>
      </div>
    </header>
  );
}

function PhoneFrame({ children, className = '' }) {
  return (
    <div className={`phone ${className}`}>
      <div className="phone-speaker" />
      <div className="phone-screen">{children}</div>
    </div>
  );
}

function Hero() {
  return (
    <section className="hero" id="top">
      <Header />
      <div className="hero-grain" />
      <img className="hero-atlas" src="/art/hero-atlas.png" alt="" />
      <div className="hero-inner">
        <div className="hero-copy reveal">
          <p className="eyebrow"><span /> Authentic traditions. One trusted table.</p>
          <h1>The world’s real card games belong together.</h1>
          <p className="hero-lede">
            Authentic regional rules. Strong, disclosed AI. Fair multiplayer.
            And one sealed Daily Table shared by everyone.
          </p>
          <div className="hero-actions">
            <a className="button button--red" href="#daily">Open the table</a>
            <a className="text-link" href="#games">Meet the founding games <span>↘</span></a>
          </div>
          <div className="hero-note">
            <span className="live-dot" />
            <p><b>Already live in private alpha.</b> Belot and Santase are running on Android.</p>
          </div>
        </div>

        <div className="hero-product reveal reveal--late" aria-label="NAIPERO Belot gameplay on Android">
          <div className="orbit orbit--one" />
          <div className="orbit orbit--two" />
          <PhoneFrame className="phone--hero">
            <img src="/screens/belot-live.png" alt="NAIPERO Belot table running on Android" />
          </PhoneFrame>
          <img className="floating-card floating-card--one" src="/cards/QH.webp" alt="" />
          <img className="floating-card floating-card--two" src="/cards/AS.webp" alt="" />
          <div className="hero-stamp">No chips<br />No wagering</div>
        </div>
      </div>
      <div className="trust-strip" aria-label="Product principles">
        <span>Server authoritative</span>
        <i>✦</i>
        <span>Built for Android</span>
        <i>✦</i>
        <span>Authentic rule profiles</span>
        <i>✦</i>
        <span>Never pay to win</span>
      </div>
    </section>
  );
}

function DailyTable() {
  return (
    <section className="section daily" id="daily">
      <div className="daily-intro reveal">
        <p className="kicker">The daily ritual</p>
        <h2>One deal.<br />One first attempt.<br /><em>The whole world.</em></h2>
        <p>
          Every UTC day begins with the same server-sealed Santase hand. Your real first
          attempt counts. Practice comes later—and yesterday’s deal can be opened and verified.
        </p>
      </div>

      <div className="daily-stage reveal reveal--late">
        <div className="stitched-route" aria-hidden="true">
          <span /><span /><span /><span />
        </div>
        <div className="daily-paper">
          <div className="daily-paper-topline">
            <span>Today’s sealed table</span>
            <span>UTC · worldwide</span>
          </div>
          <h3>SANTASE</h3>
          <p>One board. The same seat. The same Vesi.</p>
          <div className="daily-stats">
            <div><small>Attempt</small><strong>01</strong></div>
            <div><small>Reveal</small><strong>00:00</strong></div>
            <div><small>State</small><strong>Sealed</strong></div>
          </div>
          <div className="paper-button">Open today’s table <span>→</span></div>
        </div>
        <img className="wax-seal" src="/art/fairness-seal.png" alt="NAIPERO fairness seal" />
      </div>

      <div className="daily-steps">
        <article>
          <span>01</span>
          <h3>Same deal</h3>
          <p>One canonical board is committed before anyone sits down.</p>
        </article>
        <article>
          <span>02</span>
          <h3>Your first attempt</h3>
          <p>No rerolling the competitive record. Replays are clearly practice.</p>
        </article>
        <article>
          <span>03</span>
          <h3>Reveal + verify</h3>
          <p>After the window closes, inspect both hands, the talon and the proof.</p>
        </article>
      </div>
    </section>
  );
}

function ProductStory() {
  return (
    <section className="section product-story" id="alpha">
      <div className="product-visual reveal">
        <div className="phone-shadow phone-shadow--one" />
        <PhoneFrame className="phone--story phone--front">
          <img src="/screens/belot-live.png" alt="A live NAIPERO Belot hand" />
        </PhoneFrame>
        <PhoneFrame className="phone--story phone--back">
          <img src="/screens/home-games.png" alt="The NAIPERO Android home and founding games" />
        </PhoneFrame>
        <div className="caption-card">
          <span>Private alpha</span>
          <strong>Cloud-authoritative.<br />Real rules. Real reconnects.</strong>
        </div>
      </div>

      <div className="product-copy reveal reveal--late">
        <p className="kicker kicker--light">Built, not imagined</p>
        <h2>Cards do the talking.</h2>
        <p className="large-copy">
          The table stays calm. A played card travels from the real hand, the winning card
          gathers the trick, and the next move becomes clear—without layers of casino noise.
        </p>
        <div className="feature-list">
          <div>
            <b>01</b>
            <span><strong>Belot flagship</strong>Four-seat Bulgarian Bridge-Belot to 151.</span>
          </div>
          <div>
            <b>02</b>
            <span><strong>Santase complete loop</strong>Daily, casual AI and live human tables.</span>
          </div>
          <div>
            <b>03</b>
            <span><strong>Presentation, not authority</strong>The server owns every card, action and result.</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Games() {
  return (
    <section className="section games" id="games">
      <div className="section-heading reveal">
        <div>
          <p className="kicker">Founding World Collection</p>
          <h2>Eight traditions.<br />One shared standard.</h2>
        </div>
        <p>
          Each table earns its place through an authentic rules profile, capable disclosed AI,
          accessible play, cultural review and its own Daily Table format.
        </p>
      </div>

      <div className="games-grid">
        {foundingGames.map((game, index) => (
          <article className={`game-card game-card--${game.color} reveal`} style={{ '--delay': `${index * 45}ms` }} key={game.name}>
            <div className="game-topline">
              <span>{game.number}</span>
              <i aria-hidden="true">{index % 3 === 0 ? '♣' : index % 3 === 1 ? '♦' : '✦'}</i>
            </div>
            <div>
              <h3>{game.name}</h3>
              <p>{game.region}</p>
            </div>
            <small>{game.status}</small>
          </article>
        ))}
      </div>
      <p className="catalog-note reveal">A staged collection—not a same-day content drop.</p>
    </section>
  );
}

function Fairness() {
  return (
    <section className="section fairness" id="fairness">
      <div className="fairness-copy reveal">
        <p className="kicker kicker--red">Fairness, made visible</p>
        <h2>Inspect the proof.<br />Don’t trust the theatre.</h2>
        <p>
          The seal appears only when it opens a real record. NAIPERO commits the board before
          play, keeps hidden information private, reveals after the window, and verifies the
          exact deal on your device.
        </p>
        <div className="proof-footnote">
          <span>SHA-256</span>
          <p>Rules, deal, seat and bot versions are sealed together.</p>
        </div>
      </div>
      <div className="proof-flow reveal reveal--late">
        {proofSteps.map(([number, title, description]) => (
          <article key={number}>
            <span>{number}</span>
            <div><h3>{title}</h3><p>{description}</p></div>
          </article>
        ))}
        <img src="/art/fairness-seal.png" alt="" />
      </div>
    </section>
  );
}

function Craft() {
  return (
    <section className="craft">
      <div className="craft-art reveal">
        <img src="/art/regional-swatches.png" alt="Layered regional textile and card swatches" />
      </div>
      <div className="craft-copy reveal reveal--late">
        <p className="kicker kicker--light">The design constitution</p>
        <h2>Folk craft.<br /><em>Never casino.</em></h2>
        <p>
          Warm paper, printed ink, woven cloth, wax and walnut create one recognizable world.
          Researched regional textiles give every table its own voice without flattening tradition.
        </p>
        <div className="material-line">
          <span>Paper</span><i />
          <span>Ink</span><i />
          <span>Thread</span><i />
          <span>Wood</span>
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="final-cta">
      <img
        className="final-lockup"
        src="/brand/naipero-lockup.png"
        alt="NAIPERO — The World's Card Table."
      />
      <p className="kicker">Android first</p>
      <h2>The table is being set.</h2>
      <p>
        NAIPERO is in private alpha. Belot is the active flagship; Santase already supports
        sealed Daily play, Vesi AI and human Live Tables.
      </p>
      <a className="button button--red" href="#games">Explore the founding tables</a>
    </section>
  );
}

function Footer() {
  return (
    <footer>
      <Brand />
      <p>Skill, tradition and social play—never wagering.</p>
      <small>© 2026 NAIPERO. All rights reserved.</small>
    </footer>
  );
}

function App() {
  useEffect(() => {
    const nodes = document.querySelectorAll('.reveal');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      nodes.forEach((node) => node.classList.add('is-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Hero />
      <main>
        <DailyTable />
        <ProductStory />
        <Games />
        <Fairness />
        <Craft />
        <FinalCta />
      </main>
      <Footer />
    </>
  );
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
